import type { Product, Seller, User } from 'payments-api/model';
import { match, P } from 'ts-pattern';

export const PLATFORM_PAYOUT_USER = 'Platform';

type DropdownOption = { value: string; label: string };

/**
 * Type guard to check if a seller is a platform seller
 */
export const isPlatformSeller = (seller: Seller): boolean =>
  seller.soldByPlatform === true;

/**
 * Type guard to check if a seller is a user seller
 */
export const isUserSeller = (
  seller: Seller
): seller is Seller & { role: string } =>
  seller.soldByPlatform === false &&
  Object.prototype.hasOwnProperty.call(seller, 'role');

/**
 * Find a product by its code
 */
export const findProductByCode = (
  productCode: string,
  products: Product[]
): Product | undefined => products.find((p) => p.code === productCode);

/**
 * Determines if a product (by code) is a platform product
 */
export const isProductPlatformSeller = (
  productCode: string,
  products: Product[]
): boolean => {
  const product = findProductByCode(productCode, products);
  return product ? isPlatformSeller(product.seller) : false;
};

/**
 * Gets the user role for a product, or undefined if it's a platform product
 */
export const getProductUserRole = (
  productCode: string,
  products: Product[]
): string | undefined => {
  const product = findProductByCode(productCode, products);
  if (!product) return undefined;
  return isUserSeller(product.seller) ? product.seller.role : undefined;
};

/**
 * Result of determining payout party options and auto-selection for a product
 */
export type PayoutUserConfig = {
  /** The payout party options to show in the dropdown */
  options: DropdownOption[];
  /** Whether the dropdown should be disabled (for platform products) */
  isDisabled: boolean;
  /** The auto-selected payout party value (for platform products) */
  autoSelectedValue: string | undefined;
};

/**
 * Given a selected product, returns the payout party configuration:
 * - For platform products: disabled dropdown with "Platform" auto-selected
 * - For user products: enabled dropdown filtered by user role
 * - For no product selected: enabled dropdown with all users
 */
export const getPayoutUserConfig = (
  productCode: string | undefined,
  products: Product[],
  users: User[]
): PayoutUserConfig => {
  if (!productCode) {
    // No product selected - show all users plus Platform option
    return {
      options: [
        { value: PLATFORM_PAYOUT_USER, label: PLATFORM_PAYOUT_USER },
        ...users.map((u) => ({ value: u.externalId, label: u.externalId })),
      ],
      isDisabled: false,
      autoSelectedValue: undefined,
    };
  }

  const product = findProductByCode(productCode, products);
  if (!product) {
    // Product not found - show all options
    return {
      options: [
        { value: PLATFORM_PAYOUT_USER, label: PLATFORM_PAYOUT_USER },
        ...users.map((u) => ({ value: u.externalId, label: u.externalId })),
      ],
      isDisabled: false,
      autoSelectedValue: undefined,
    };
  }

  return match(product.seller)
    .with({ soldByPlatform: true }, () => ({
      // Platform product - disable dropdown and auto-select Platform
      options: [{ value: PLATFORM_PAYOUT_USER, label: PLATFORM_PAYOUT_USER }],
      isDisabled: true,
      autoSelectedValue: PLATFORM_PAYOUT_USER,
    }))
    .with({ soldByPlatform: false, role: P.string }, (seller) => {
      // User product - filter users by role matching user role
      const filteredUsers = users.filter((u) => u.role === seller.role);
      return {
        options: filteredUsers.map((u) => ({
          value: u.externalId,
          label: u.externalId,
        })),
        isDisabled: false,
        autoSelectedValue: undefined,
      };
    })
    .otherwise(() => ({
      // Fallback - show all users
      options: users.map((u) => ({
        value: u.externalId,
        label: u.externalId,
      })),
      isDisabled: false,
      autoSelectedValue: undefined,
    }));
};

/**
 * Handles the logic when a product is selected for an invoice item.
 * Returns the updates that should be applied to the item.
 */
export const getProductSelectionUpdates = (
  productCode: string,
  products: Product[]
): { payoutUser: string } | undefined => {
  const product = findProductByCode(productCode, products);
  if (!product) return undefined;

  if (isPlatformSeller(product.seller)) {
    // Auto-set payout user to Platform for platform products
    return { payoutUser: PLATFORM_PAYOUT_USER };
  }

  // For user products, clear the payout user so user must select
  return { payoutUser: '' };
};
